import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/apiService'

const AddOfficial = () => {
  const navigate = useNavigate()
  const [states, setStates] = useState([])
  const [districts, setDistricts] = useState([])
  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    full_name: '',
    father_name: '',
    date_of_birth: '',
    gender: 'M',
    blood_group: '',
    height: '',
    weight: '',
    address: '',
    pin_code: '',
    district: '',
    state_name: '',
    official_type: 'state_official',
    aadhar_card: '',
    passport_number: '',
    primary_contact: '',
    secondary_contact: '',
    email: '',
    marital_status: ''
  })

  const officialTypes = [
    { value: 'state_official', label: 'State Official' },
    { value: 'tournament_official', label: 'Tournament Official' },
    { value: 'other_official', label: 'Other Official' }
  ]

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

  useEffect(() => {
    fetchStates()
  }, [])

  const fetchStates = async () => {
    try {
      const response = await api.get('/client-admin/users/states')
      console.log('States API response:', response.data)
      if (response.data.success && response.data.data) {
        setStates(response.data.data)
      } else if (response.data.data) {
        setStates(response.data.data)
      } else if (Array.isArray(response.data)) {
        setStates(response.data)
      }
    } catch (error) {
      console.error('Error fetching states:', error)
    }
  }

  const fetchDistricts = async (stateId) => {
    if (!stateId) {
      setDistricts([])
      return
    }
    try {
      const response = await api.get(`/client-admin/users/states/${stateId}/districts`)
      console.log('Districts API response:', response.data)
      if (response.data.success && response.data.data) {
        setDistricts(response.data.data)
      } else if (response.data.data) {
        setDistricts(response.data.data)
      } else if (Array.isArray(response.data)) {
        setDistricts(response.data)
      }
    } catch (error) {
      console.error('Error fetching districts:', error)
      setDistricts([])
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // Validate age when DOB changes (official must be 18+)
    if (name === 'date_of_birth' && value) {
      const birthDate = new Date(value)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }

      // Check if official is at least 18 years old - show error immediately
      if (age < 18) {
        setErrors(prev => ({ ...prev, date_of_birth: 'Official must be at least 18 years old' }))
      } else {
        setErrors(prev => ({ ...prev, date_of_birth: '' }))
      }
    }

    // When state changes, fetch districts for that state
    if (name === 'state_name') {
      const selectedState = states.find(s => s.name === value)
      if (selectedState) {
        fetchDistricts(selectedState.id)
      } else {
        setDistricts([])
      }
      // Clear district when state changes
      setFormData(prev => ({ ...prev, [name]: value, district: '' }))
    } else {
      setFormData(prev => ({ ...prev, [name]: value }))
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size should be less than 2MB' }))
        return
      }

      setErrors(prev => ({ ...prev, image: '' }))
      setImageFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!imageFile) newErrors.image = 'Photo is required'
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required'
    if (!formData.father_name.trim()) newErrors.father_name = "Father's name is required"
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required'
    } else {
      // Check if official is at least 18 years old
      const birthDate = new Date(formData.date_of_birth)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      if (age < 18) {
        newErrors.date_of_birth = 'Official must be at least 18 years old'
      }
    }
    if (!formData.state_name) newErrors.state_name = 'State is required'
    if (!formData.gender) newErrors.gender = 'Gender is required'
    if (!formData.official_type) newErrors.official_type = 'Official type is required'

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (formData.primary_contact && !/^\d{10}$/.test(formData.primary_contact)) {
      newErrors.primary_contact = 'Primary contact must be 10 digits'
    }

    if (formData.secondary_contact && !/^\d{10}$/.test(formData.secondary_contact)) {
      newErrors.secondary_contact = 'Secondary contact must be 10 digits'
    }

    if (formData.aadhar_card && !/^\d{12}$/.test(formData.aadhar_card)) {
      newErrors.aadhar_card = 'Aadhar must be 12 digits'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const calculateAge = () => {
    if (!formData.date_of_birth) return null
    const birthDate = new Date(formData.date_of_birth)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const currentAge = calculateAge()

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      alert('Please fill all required fields correctly')
      return
    }

    setLoading(true)

    try {
      const formDataToSend = new FormData()

      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key])
        }
      })

      // Append image if selected
      if (imageFile) {
        formDataToSend.append('official_image', imageFile)
      }

      // Don't set Content-Type header - axios will set it automatically with boundary for FormData
      const response = await api.post('/client-admin/officials', formDataToSend)

      if (response.data.success) {
        alert('Official added successfully!')
        navigate('/admin/officials')
      }
    } catch (error) {
      console.error('Error creating official:', error)
      alert(error.response?.data?.message || 'Failed to create official')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="add-official-page">
      {/* Page Header with gradient background */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-user-tie"></i>
          </div>
          <div className="header-text">
            <h1>Add New Official</h1>
            <p>Register a new official to the system</p>
          </div>
        </div>
        <button className="btn-back" style={{ marginLeft: 'auto' }} onClick={() => navigate('/admin/officials')}>
          <i className="fas fa-arrow-left"></i>
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Image Upload Section */}
          <div className="image-upload-section">
            <div className="image-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Official" />
              ) : (
                <div className="placeholder">
                  <i className="fas fa-user-circle"></i>
                  <p>Upload Photo</p>
                </div>
              )}
            </div>
            <div className="image-upload-controls">
              <label className="btn-upload">
                <i className="fas fa-camera"></i> Choose Photo <span style={{ color: '#ffd1d1', marginLeft: '2px' }}>*</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </label>
              <small>JPG, PNG, GIF (Max 2MB)</small>
              {errors.image && (
                <div className="image-error-text">
                  <i className="fas fa-exclamation-circle"></i> {errors.image}
                </div>
              )}
            </div>
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h3><i className="fas fa-user"></i> Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Enter official's full name"
                  className={errors.full_name ? 'error' : ''}
                  required
                />
                {errors.full_name && <span className="error-text">{errors.full_name}</span>}
              </div>

              <div className="form-group">
                <label>Father's Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="father_name"
                  value={formData.father_name}
                  onChange={handleInputChange}
                  placeholder="Enter father's name"
                  className={errors.father_name ? 'error' : ''}
                  required
                />
                {errors.father_name && <span className="error-text">{errors.father_name}</span>}
              </div>

              <div className="form-group">
                <label>Date of Birth <span className="required">*</span></label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]}
                  className={errors.date_of_birth ? 'error' : ''}
                  required
                />
                {errors.date_of_birth && <span className="error-text">{errors.date_of_birth}</span>}
                {currentAge !== null && (
                  <small className="info-text">
                    Age: {currentAge} years
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Gender <span className="required">*</span></label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className={errors.gender ? 'error' : ''}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
                {errors.gender && <span className="error-text">{errors.gender}</span>}
              </div>

              <div className="form-group">
                <label>Blood Group</label>
                <select
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleInputChange}
                >
                  <option value="">Select Blood Group</option>
                  {bloodGroups.map(bg => (
                    <option key={bg} value={bg}>{bg}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Height (cm)</label>
                <input
                  type="number"
                  name="height"
                  value={formData.height}
                  onChange={handleInputChange}
                  placeholder="Height in cm"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Weight (kg)</label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="Weight in kg"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label>Marital Status</label>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleInputChange}
                >
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="form-section">
            <h3><i className="fas fa-phone"></i> Contact Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Primary Contact Number</label>
                <input
                  type="tel"
                  name="primary_contact"
                  value={formData.primary_contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    setFormData(prev => ({ ...prev, primary_contact: value }))
                    if (errors.primary_contact) setErrors(prev => ({ ...prev, primary_contact: '' }))
                  }}
                  placeholder="10-digit primary contact number"
                  maxLength="10"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={errors.primary_contact ? 'error' : ''}
                />
                {errors.primary_contact && <span className="error-text">{errors.primary_contact}</span>}
              </div>

              <div className="form-group">
                <label>Secondary Contact Number</label>
                <input
                  type="tel"
                  name="secondary_contact"
                  value={formData.secondary_contact}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                    setFormData(prev => ({ ...prev, secondary_contact: value }))
                    if (errors.secondary_contact) setErrors(prev => ({ ...prev, secondary_contact: '' }))
                  }}
                  placeholder="10-digit secondary contact number"
                  maxLength="10"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={errors.secondary_contact ? 'error' : ''}
                />
                {errors.secondary_contact && <span className="error-text">{errors.secondary_contact}</span>}
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="official@example.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="form-section">
            <h3><i className="fas fa-map-marker-alt"></i> Address Information</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter complete address"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>State <span className="required">*</span></label>
                <select
                  name="state_name"
                  value={formData.state_name}
                  onChange={handleInputChange}
                  className={errors.state_name ? 'error' : ''}
                  required
                >
                  <option value="">Select State</option>
                  {states.map(state => (
                    <option key={state.id} value={state.name}>{state.name}</option>
                  ))}
                </select>
                {errors.state_name && <span className="error-text">{errors.state_name}</span>}
              </div>

              <div className="form-group">
                <label>District</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  disabled={!formData.state_name}
                >
                  <option value="">{formData.state_name ? 'Select District' : 'Select State First'}</option>
                  {districts.map(district => (
                    <option key={district.id} value={district.name}>{district.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>PIN Code</label>
                <input
                  type="text"
                  name="pin_code"
                  value={formData.pin_code}
                  onChange={handleInputChange}
                  placeholder="6-digit PIN code"
                  maxLength="6"
                />
              </div>
            </div>
          </div>

          {/* Official Information */}
          <div className="form-section">
            <h3><i className="fas fa-id-badge"></i> Official Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Official Type <span className="required">*</span></label>
                <select
                  name="official_type"
                  value={formData.official_type}
                  onChange={handleInputChange}
                  className={errors.official_type ? 'error' : ''}
                  required
                >
                  <option value="">Select Official Type</option>
                  {officialTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                {errors.official_type && <span className="error-text">{errors.official_type}</span>}
              </div>
            </div>
          </div>

          {/* Identity Information */}
          <div className="form-section">
            <h3><i className="fas fa-id-card"></i> Identity Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Aadhar Card Number</label>
                <input
                  type="text"
                  name="aadhar_card"
                  value={formData.aadhar_card}
                  onChange={handleInputChange}
                  placeholder="12-digit Aadhar number"
                  maxLength="12"
                  className={errors.aadhar_card ? 'error' : ''}
                />
                {errors.aadhar_card && <span className="error-text">{errors.aadhar_card}</span>}
              </div>

              <div className="form-group">
                <label>Passport Number</label>
                <input
                  type="text"
                  name="passport_number"
                  value={formData.passport_number}
                  onChange={handleInputChange}
                  placeholder="Passport number (if available)"
                  maxLength="20"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/admin/officials')}
              disabled={loading}
            >
              <i className="fas fa-times"></i> Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Add Official
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .add-official-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: #f3f4f6;
        }

        .page-header-banner {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 0;
          width: 100%;
        }

        .btn-back {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.3);
          background: rgba(255,255,255,0.1);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .btn-back:hover {
          background: rgba(255,255,255,0.2);
          border-color: rgba(255,255,255,0.5);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .header-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .header-text h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .header-text p {
          color: rgba(255,255,255,0.7);
          margin: 0;
          font-size: 0.875rem;
        }

        .form-container {
          background: white;
          margin: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .image-upload-section {
          display: flex;
          gap: 2rem;
          padding: 2rem;
          background: #f9fafb;
          align-items: center;
          border-bottom: 1px solid #e5e7eb;
        }

        .image-preview {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          overflow: hidden;
          border: 4px solid var(--primary-color, #022a5e);
          background: white;
          flex-shrink: 0;
        }

        .image-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          color: #9ca3af;
        }

        .placeholder i {
          font-size: 3rem;
          margin-bottom: 0.5rem;
          color: var(--primary-color, #022a5e);
          opacity: 0.5;
        }

        .placeholder p {
          font-size: 0.875rem;
        }

        .image-upload-controls {
          flex: 1;
        }

        .btn-upload {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: var(--primary-color, #022a5e);
          color: white;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .btn-upload:hover {
          background: var(--primary-hover, #053e88);
        }

        .image-upload-controls small {
          display: block;
          color: #6b7280;
          margin-top: 0.5rem;
        }

        .image-error-text {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 0.75rem;
          padding: 0.5rem 0.75rem;
          color: #b91c1c;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .form-section {
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section:last-of-type {
          border-bottom: none;
        }

        .form-section h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 1.25rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section h3 i {
          color: var(--primary-color, #022a5e);
          font-size: 0.9rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: span 2;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .required {
          color: #ef4444;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.2s;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #9ca3af;
        }

        .form-group input.error,
        .form-group select.error {
          border-color: #ef4444;
        }

        .form-group input.auto-field {
          background: #f3f4f6;
          cursor: not-allowed;
          color: var(--primary-color, #022a5e);
          font-weight: 500;
        }

        .error-text {
          color: #ef4444;
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }

        .info-text {
          color: #6b7280;
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }

        .form-actions {
          padding: 1.5rem 2rem;
          background: #f9fafb;
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel {
          padding: 0.75rem 1.5rem;
          background: white;
          color: #6b7280;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-submit {
          padding: 0.75rem 1.5rem;
          background: var(--primary-color, #022a5e);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-submit:hover:not(:disabled) {
          background: var(--primary-hover, #053e88);
        }

        .btn-submit:disabled,
        .btn-cancel:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-container {
            margin: 1rem;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-group.full-width {
            grid-column: span 1;
          }

          .form-section {
            padding: 1.25rem 1.5rem;
          }

          .image-upload-section {
            flex-direction: column;
            text-align: center;
          }

          .form-actions {
            flex-direction: column-reverse;
          }

          .btn-submit,
          .btn-cancel {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  )
}

export default AddOfficial
